import React, { useState, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { QRCodeCanvas } from 'qrcode.react';
import { Download, RefreshCw, Printer } from 'lucide-react';
import { toast } from 'sonner';
import {
  GET_EMPLOYEE_QR_CODE,
  REGENERATE_EMPLOYEE_QR_CODE,
} from '../../graphql/queries';

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  pin: string;
  employeeCode?: string;
}

interface EmployeeQRCodeModalProps {
  employee: Employee | null;
  open: boolean;
  onClose: () => void;
}

export const EmployeeQRCodeModal: React.FC<EmployeeQRCodeModalProps> = ({
  employee,
  open,
  onClose,
}) => {
  const qrRef = useRef<HTMLDivElement>(null);
  const [regenerating, setRegenerating] = useState(false);

  const { data, loading, refetch } = useQuery(GET_EMPLOYEE_QR_CODE, {
    variables: { employeeId: employee?.id },
    skip: !employee?.id || !open,
  });

  const [regenerateQRCode] = useMutation(REGENERATE_EMPLOYEE_QR_CODE);

  const qrCodeData = data?.employeeQRCode;

  const handleDownload = () => {
    if (!qrRef.current) return;

    const canvas = qrRef.current.querySelector('canvas');
    if (!canvas) {
      toast.error('Nie można pobrać kodu QR');
      return;
    }

    // Convert canvas to blob and download
    canvas.toBlob((blob) => {
      if (!blob) {
        toast.error('Nie można wygenerować obrazu QR');
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `qr-${employee?.firstName}-${employee?.lastName}.png`;
      link.click();
      URL.revokeObjectURL(url);

      toast.success('Kod QR został pobrany');
    });
  };

  const handlePrint = () => {
    if (!qrRef.current) return;

    const printWindow = window.open('', '', 'width=600,height=800');
    if (!printWindow) {
      toast.error('Nie można otworzyć okna wydruku');
      return;
    }

    const qrCanvas = qrRef.current.querySelector('canvas');
    if (!qrCanvas) {
      toast.error('Nie można wydrukować kodu QR');
      return;
    }

    const qrImageUrl = qrCanvas.toDataURL('image/png');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Kod QR - ${employee?.firstName} ${employee?.lastName}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 40px;
            }
            h1 {
              margin: 0 0 10px 0;
              font-size: 24px;
            }
            p {
              margin: 5px 0;
              color: #666;
            }
            .qr-container {
              margin: 30px 0;
              padding: 20px;
              border: 2px solid #168dc8;
              border-radius: 8px;
            }
            .footer {
              margin-top: 30px;
              font-size: 12px;
              color: #999;
            }
            @media print {
              body {
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          <h1>${employee?.firstName} ${employee?.lastName}</h1>
          <p>Email: ${employee?.email}</p>
          ${employee?.employeeCode ? `<p>Kod pracownika: ${employee?.employeeCode}</p>` : ''}
          <div class="qr-container">
            <img src="${qrImageUrl}" alt="QR Code" style="width: 300px; height: 300px;" />
          </div>
          <p class="footer">SZKŁO Sp. z o.o. - Workflow System</p>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handlePrintBusinessCard = () => {
    if (!qrRef.current) return;

    const printWindow = window.open('', '', 'width=800,height=1000');
    if (!printWindow) {
      toast.error('Nie można otworzyć okna wydruku');
      return;
    }

    const qrCanvas = qrRef.current.querySelector('canvas');
    if (!qrCanvas) {
      toast.error('Nie można wydrukować kodu QR');
      return;
    }

    const qrImageUrl = qrCanvas.toDataURL('image/png');

    // Generate 12 credit-card sized cards (3x4) on one A4 page
    // Credit card size: 85.6mm x 53.98mm
    // Our size: 80mm x 49mm (5mm smaller for lamination margin)
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Kody QR - ${employee?.firstName} ${employee?.lastName}</title>
          <style>
            @page {
              size: A4;
              margin: 8mm;
            }

            * {
              box-sizing: border-box;
            }

            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 0;
              background: white;
            }

            .page {
              width: 210mm;
              height: 297mm;
              display: grid;
              grid-template-columns: repeat(3, 80mm);
              grid-template-rows: repeat(4, 49mm);
              gap: 3mm;
              padding: 3mm;
              justify-content: center;
              align-content: center;
            }

            .card {
              width: 80mm;
              height: 49mm;
              border: 1px dashed #ccc;
              border-radius: 3mm;
              padding: 3mm;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: space-between;
              background: white;
              page-break-inside: avoid;
              position: relative;
              overflow: hidden;
            }

            .card-header {
              text-align: center;
              width: 100%;
            }

            .card-header h2 {
              margin: 0;
              font-size: 10pt;
              font-weight: bold;
              color: #168dc8;
              line-height: 1.1;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }

            .card-header .code {
              margin: 0.5mm 0 0 0;
              font-size: 7pt;
              color: #666;
              font-weight: normal;
            }

            .qr-container {
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            }

            .qr-container img {
              display: block;
              width: 30mm;
              height: 30mm;
            }

            .card-footer {
              text-align: center;
              width: 100%;
            }

            .card-footer .company {
              font-size: 8pt;
              font-weight: bold;
              color: #168dc8;
              margin: 0;
              line-height: 1;
            }

            .card-footer .instruction {
              font-size: 5.5pt;
              color: #999;
              margin: 0.5mm 0 0 0;
              line-height: 1.2;
            }

            /* Cutting guides */
            .card::before,
            .card::after {
              content: '';
              position: absolute;
              background: #ddd;
            }

            @media print {
              body {
                margin: 0;
                padding: 0;
              }

              .card {
                border-color: #999;
              }

              @page {
                margin: 8mm;
              }
            }
          </style>
        </head>
        <body>
          <div class="page">
            ${[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(() => `
              <div class="card">
                <div class="card-header">
                  <h2>${employee?.firstName} ${employee?.lastName}</h2>
                  ${employee?.employeeCode ? `<p class="code">Kod: ${employee?.employeeCode}</p>` : ''}
                </div>

                <div class="qr-container">
                  <img src="${qrImageUrl}" alt="QR Code" />
                </div>

                <div class="card-footer">
                  <div class="company">SZKŁO Sp. z o.o.</div>
                  <div class="instruction">Zeskanuj na tablecie</div>
                </div>
              </div>
            `).join('')}
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.onafterprint = function() {
                  window.close();
                };
              }, 250);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleRegenerate = async () => {
    if (!employee?.id) return;

    try {
      setRegenerating(true);
      await regenerateQRCode({
        variables: { employeeId: employee.id },
      });
      await refetch();
      toast.success('Kod QR został wygenerowany ponownie');
    } catch (error: any) {
      toast.error(error.message || 'Nie udało się wygenerować kodu QR');
    } finally {
      setRegenerating(false);
    }
  };

  if (!employee) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            Kod QR dla {employee.firstName} {employee.lastName}
          </DialogTitle>
          <DialogDescription>
            Użyj tego kodu QR do szybkiego logowania na urządzeniach typu tablet
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          )}

          {/* QR Code Display */}
          {!loading && qrCodeData && (
            <>
              <div className="flex justify-center" ref={qrRef}>
                <div className="p-6 bg-white border-2 border-primary rounded-lg">
                  <QRCodeCanvas
                    value={qrCodeData.qrData}
                    size={256}
                    level="H"
                    includeMargin={false}
                  />
                </div>
              </div>

              {/* Employee Info */}
              <div className="bg-gray-50 p-4 rounded-md text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="font-semibold">Imię i nazwisko:</span>
                  </div>
                  <div>{employee.firstName} {employee.lastName}</div>

                  <div>
                    <span className="font-semibold">Email:</span>
                  </div>
                  <div>{employee.email}</div>

                  {employee.employeeCode && (
                    <>
                      <div>
                        <span className="font-semibold">Kod pracownika:</span>
                      </div>
                      <div>{employee.employeeCode}</div>
                    </>
                  )}

                  <div>
                    <span className="font-semibold">PIN:</span>
                  </div>
                  <div>{'●'.repeat(4)}</div>

                  <div>
                    <span className="font-semibold">Wygenerowano:</span>
                  </div>
                  <div>
                    {new Date(qrCodeData.timestamp).toLocaleString('pl-PL')}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={handleDownload}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Pobierz PNG
                  </Button>

                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={handleRegenerate}
                    disabled={regenerating}
                  >
                    <RefreshCw
                      className={`w-4 h-4 mr-2 ${regenerating ? 'animate-spin' : ''}`}
                    />
                    Regeneruj
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={handlePrint}
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Drukuj (1 QR)
                  </Button>

                  <Button
                    variant="default"
                    className="flex-1"
                    onClick={handlePrintBusinessCard}
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Drukuj karty (12 szt.)
                  </Button>
                </div>
              </div>

              {/* Information */}
              <div className="bg-blue-50 border border-blue-200 p-3 rounded-md text-sm">
                <p className="text-blue-800">
                  <strong>Jak używać kodu QR:</strong>
                </p>
                <ul className="list-disc list-inside mt-2 text-blue-700 space-y-1">
                  <li>Wydrukuj kod QR i prześlij pracownikowi</li>
                  <li>Pracownik może używać go do logowania na tabletach</li>
                  <li>Kod jest bezpiecznie zaszyfrowany i unikalny</li>
                  <li>Możesz w każdej chwili wygenerować nowy kod</li>
                </ul>
              </div>
            </>
          )}

          {/* Error State */}
          {!loading && !qrCodeData && (
            <div className="text-center py-12">
              <p className="text-red-500 mb-4">
                Nie udało się wygenerować kodu QR
              </p>
              <Button onClick={() => refetch()}>Spróbuj ponownie</Button>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Zamknij
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
